import * as _ from 'lodash';

import { Injectable } from '@angular/core';
import { LookupMetadataService } from './lookup-metadata.service';
import { share, filter, switchMap, map } from 'rxjs/operators';
import { Observable, merge } from 'rxjs';
import { LookupItem } from '../models/lookup-item';
import { ApiClientService } from '../../api-client';
import { LookupMetadata } from '../models/lookup-metadata';
import { Cache} from './cache';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  private cache = new Cache();

  private readonly microCacheDuration = 60000;
  private readonly cacheDuration = 600000;

  constructor(
    private lookupMetadataService: LookupMetadataService,
    private apiClientService: ApiClientService
  ) {
  }

  public getLookups(lookupName: string, value?: number | number[]): Observable<LookupItem[] | LookupItem> {
    const source = this.lookupMetadataService.getLookupMetadataByName(lookupName).pipe(share());

    const uncacheable = source.pipe(
      filter(lookup => !lookup.cacheable),
      switchMap(lookup => {
        if (!!value && typeof value === 'number') {
          return this.cache.get(`${lookup.id}-${value}`, this.getLookupValueById(lookup.id, value), this.microCacheDuration);
        } else {
          return this.cache.get(`${lookup.id}`, this.getLookupValues(lookup), this.microCacheDuration);
        }
      })
    );

    const cacheable = source.pipe(
      filter(lookup => !!lookup.cacheable),
      switchMap(lookup => this.cache.get(`${lookup.id}`, this.getLookupValues(lookup), this.cacheDuration)),
      map(lookupItems => {
        if (!!value && typeof value === 'number') {
          return lookupItems.find(item => item.id === value) as LookupItem;
        } else {
          return lookupItems;
        }
      })
    );

    return merge(uncacheable, cacheable);
  }

  public getLookupValues(lookup: LookupMetadata): Observable<LookupItem[]> {
    return this.getLookupValuesById(lookup.id);
  }

  private getLookupValueById(id: number, lookupItemId: number) {
    const httpParams = new HttpParams().append('lookupItemId', lookupItemId.toString());
    return this.apiClientService.get<LookupItem>(`/core/lookups/${id}`, {
      params: httpParams
    });
  }

  public getLookupValuesById(id: number) {
    return this.apiClientService.get<LookupItem[]>(`/core/lookups/${id}`);
  }

  public getDefaultLookupName(fieldName: string | null) {
    if (!fieldName) {
      return null;
    }
    const fields = fieldName.split('.');
    let field: string;
    if (fields.length > 0) {
      field = fields[fields.length - 1];
    } else {
      field = fieldName;
    }

    return _.startCase(field.replace(/Id$/gi, ''));
  }

  public saveLookup(lookupId: number, entity: LookupItem) {
    return this.saveLookupItem(lookupId, entity);
  }

  private saveLookupItem(lookupId: number, lookupItem: LookupItem) {
    if (lookupItem.id && lookupItem.id >= 0) {
      return this.updateLookupItem(lookupId, lookupItem);
    } else {
      return this.addLookupItem(lookupId, lookupItem);
    }
  }

  private addLookupItem(lookupId: number, lookupItem: LookupItem) {
    return this.apiClientService.post<any>(`/core/lookups/${lookupId}/items`, lookupItem);
  }

  private updateLookupItem(lookupId: number, lookupItem: LookupItem) {
    return this.apiClientService.put(`/core/lookups/${lookupId}/items/${lookupItem.id}`, lookupItem);
  }

  public getEntity(id: number) {
    if (id && id >= 0) {
      return this.apiClientService.get<LookupItem[]>('/core/lookups').pipe(map(items => {
        return items.filter(item => {
          return item.id === id;
        })[0];
      }));
    } else {
      return this.apiClientService.get<LookupItem[]>('/core/lookups').pipe(map(() => {
        return <LookupItem>{
          name: ''
        };
      }));
    }
  }

  public clearCache(): void {
    this.cache.clear();
  }
}
